<template>
  <div class="bank-select-container">
    <!-- 顶部导航 -->
    <Navbar title="选择银行" :isSpecialBack="true" @goBack="goBack" />

    <!-- 搜索框（固定在顶部） -->
    <div class="search-wrapper">
      <van-search
        v-model.trim="searchQuery"
        placeholder="输入银行名称或拼音首字母搜索"
        clearable
      />
    </div>

    <!-- A-Z 字母索引栏 -->
    <van-index-bar
      v-if="sortedBanks.length > 0"
      :index-list="dynamicIndexList"
      highlight-color="#1D70F7"
    >
      <div v-for="group in sortedBanks" :key="group.letter">
        <van-index-anchor :index="group.letter" />

        <!-- 银行列表项 -->
        <van-cell
          v-for="bank in group.list"
          :key="bank.id"
          :title="bank.bankName"
          is-link
          center
          @click="onSelectBank(bank)"
        >
          <!-- 优化：使用 van-image 替代原生 img，自带加载中/失败占位，彻底防闪烁 -->
          <template #icon>
            <van-image
              round
              width="24px"
              height="24px"
              :src="bank.logo"
              class="bank-logo"
            >
              <!-- 加载失败的兜底图：防止页面留白难看 -->
              <template #error>
                <div class="bank-logo-placeholder">{{ bank.bankName[0] }}</div>
              </template>
            </van-image>
          </template>
        </van-cell>
      </div>
    </van-index-bar>

    <!-- 搜索无结果时的缺省提示 -->
    <van-empty v-else description="未找到相关银行" />
  </div>
</template>

<script>
import { IndexBar, IndexAnchor, Cell, Search, Image as VanImage, Empty } from "vant";
import Navbar from "@/components/navbar/index.vue";
import { getBankList } from "@/api/index";

export default {
  name: "BankSelect",
  components: {
    Navbar,
    [IndexBar.name]: IndexBar,
    [IndexAnchor.name]: IndexAnchor,
    [Cell.name]: Cell,
    [Search.name]: Search,
    [VanImage.name]: VanImage,
    [Empty.name]: Empty,
  },
  data() {
    return {
      searchQuery: "",
      rawBankList: [], // 存储后端返回的干净原始数据
    };
  },
  computed: {
    // 过滤与分组全部托管给计算属性
    sortedBanks() {
      const query = this.searchQuery.trim().toLowerCase();
      
      // 1. 如果有搜索词，先对原始数据做过滤
      const filtered = this.rawBankList.filter(bank => {
        if (!query) return true;
        
        // 动态利用插件预留的隐藏字段进行【汉字/首字母/全拼】三合一高级模糊搜索
        return (
          bank.bankName.toLowerCase().includes(query) ||
          bank._letter.toLowerCase().includes(query) ||
          bank._fullPinyin.includes(query)
        );
      });

      // 2. 直接调用全局插件，一键完成 A-Z 分组排序
      return this.$pinyinUtils.groupAndSort(filtered, "bankName");
    },

    // 右侧侧边栏索引直接跟随分组动态映射
    dynamicIndexList() {
      return this.sortedBanks.map(group => group.letter);
    },
  },
  mounted() {
    this.fetchBankData();
  },
  methods: {
    async fetchBankData() {
      try {
        const res = await getBankList({ pageNumber: 1, pageSize: 1000 });
        if (res?.code === 2000 && Array.isArray(res.data?.recordList)) {
          // 预处理原始数据：为了搜索响应更快，提前把每一项的隐藏拼音字段算好
          this.rawBankList = res.data.recordList.map(item => ({
            ...item,
            _letter: this.$pinyinUtils.getLetter(item.bankName),
            _fullPinyin: this.$pinyinUtils.getLetter(item.bankName) === '#' ? '' : item.bankName
          }));
        }
      } catch (error) {
        console.error("获取银行列表失败:", error);
      }
    },
    onSelectBank(bank) {
      this.$store.commit("setBankName", bank.bankName);
      this.$router.back();
    },
    goBack() {
      this.$router.back();
    }
  }
};
</script>

<style scoped lang="scss">
.bank-select-container {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  overflow-y: auto;
  background-color: #f7f8fa;
}

/* 保证搜索框固定在顶部不随列表滚动 */
.search-wrapper {
  position: sticky;
  top: var(--sticky-top); // 吸顶56px
  z-index: 99;
}

/* 银行 Logo 样式调整 */
.bank-logo {
  margin-right: 12px;
  display: flex;
  align-items: center;
}

/* 提高右侧索引栏的层级，防止被 sticky 元素遮挡 */
::v-deep .van-index-bar__sidebar {
  z-index: 100;
  top: 60%; /* 适当下移，防止挡住搜索框位置 */
}
// 建议配合公共样式补充几个微调样式
.bank-logo {
  margin-right: 12px; // 修正 Vant Cell 插槽默认没有右间距的问题
}

// 当 logo 加载失败时展示的文字头像兜底（美观且不闪烁）
.bank-logo-placeholder {
  width: 100%;
  height: 100%;
  background-color: #eef1f6;
  color: #1d70f7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 13px;
}
</style>
